<script setup lang="ts">
import { useWindowScroll } from '@vueuse/core'
import CartBtnComponent from '@/components/games/CartBtnComponent.vue'
import seperatorImg from '/src/assets/components/pageHeader/separator.webp?url'
import UserIcon from '/src/assets/components/pageHeader/user.webp?url'
import LogoSite from '/src/assets/store_logo_min.png'

const { windowWidth, isDesktop, screens, getLocalePath } = tools
const { gameOrder, list, isLogged, routeGame, getRoutePathToGame } = gamesUtils
const { t } = useI18n()

const isExpand = ref(false)
const { y: windowScrollY } = useWindowScroll()
const scrollObserver = ref({
  y: 0,
  yPrev: 0,
  isHiddenHeader: false
})
const route = useRoute()
function initWatchScrollY() {
  if (windowWidth.value < 900) {
    return watch(
      () => windowScrollY.value,
      (scrollY) => {
        scrollObserver.value.isHiddenHeader = !(scrollY <= 250 || scrollObserver.value.yPrev > scrollY)
        scrollObserver.value.yPrev = scrollY
      }
    )
  }
  return null
}
let unWatchScrollY = initWatchScrollY()
watch(
  () => windowWidth.value,
  () => {
    if (windowWidth.value >= 900 && unWatchScrollY !== null) {
      unWatchScrollY()
      unWatchScrollY = null
    } else if (unWatchScrollY === null) {
      unWatchScrollY = initWatchScrollY()
    }
  }
)

interface MenuItem {
  title: string
  path: string
  routeGroupName: string
  gameId?: string
}

const gamesMenu: Array<MenuItem> = gameOrder
  .filter((gameKey) => {
    return list?.[gameKey]?.active
  })
  .map((gameKey) => {
    return { title: list?.[gameKey]?.name ?? '', path: getRoutePathToGame(gameKey), routeGroupName: 'game-page', gameId: list?.[gameKey]?.id }
  })
const sliceSize = computed(() => {
  let slice = 4
  if (isLogged.value) {
    if (screens.value.big.alLeast && !screens.value.lg.alLeast) {
      slice = 2
    }
    if (screens.value.lg.alLeast && !screens.value.xl.alLeast) {
      slice = 4
    }
    if (screens.value.xl.alLeast) {
      slice = 5
    }
  } else {
    if (screens.value.big.alLeast && !screens.value.lg.alLeast) {
      slice = 2
    }
    if (screens.value.lg.alLeast && !screens.value.xl.alLeast) {
      slice = 3
    }
    if (screens.value.xl.alLeast) {
      slice = 5
    }
  }
  return Math.min(gamesMenu.length, slice)
})
const infoPages = computed<Array<MenuItem>>(() => [
  { title: t('header.about'), routeGroupName: 'about-page', path: getLocalePath('about-page') },
  { title: t('header.help'), routeGroupName: 'help-page', path: getLocalePath('help-page') },
  { title: t('header.special'), routeGroupName: 'activate-goat-offer', path: getLocalePath('activate-goat-offer') }
])

const menuItems = computed(() => {
  return gamesMenu.slice(0, sliceSize.value)
})

const otherItems = computed<Array<MenuItem>>(() => {
  const items: MenuItem[] = []
  items.push(...gamesMenu.slice(sliceSize.value))
  items.push(...infoPages.value)
  return items
})

function onClickMenuLink() {
  isExpand.value = false
}

const isOtherPageActive = computed(() => {
  return otherItems.value.some(item => route.path === item.path)
})

watch(
  () => `${isDesktop.value}_${isExpand.value}`,
  () => {
    if (!isDesktop.value && isExpand.value) {
      document.body.classList.add('overflow-hidden')
    } else {
      document.body.classList.remove('overflow-hidden')
    }
  }
)
</script>

<template>
  <div
    v-if="isExpand && !isDesktop"
    class="bg-black/70 opacity-90 fixed inset-0 z-60"
    @click="isExpand = !isExpand"
  />
  <header
    class="fixed top-0 z-60 max-h-[90vh] w-full overflow-auto bg-white pt-3 big:py-3 big:overflow-visible min-h-[60px]"
    :class="{ 'is-hidden': !isDesktop && scrollObserver.isHiddenHeader }"
  >
    <div
      class="big:min-h-[63px] mx-auto flex flex-col big:flex-row h-full max-w-[1400px] justify-between big:justify-start px-0 big:px-2 lg:px-8 items-center leading-none"
    >
      <div class="w-full big:w-auto flex justify-between pl-3 big:pl-0 leading-none">
        <router-link
          :to="getLocalePath('home-page')"
          @click="isExpand = false"
        >
          <img
            :src="LogoSite"
            class="mr-1 h-9 big:h-10 xl:h-12 big:py-1"
            :alt="t('header.logoAlt')"
          >
        </router-link>
        <div class="flex items-center gap-x-2 justify-center big:hidden">
          <div class="mr-2 sm:mr-4">
            <language-selector-component use-popover />
          </div>
          <CartBtnComponent
            icon-class="text-2xl"
            class="size-10 flex justify-center items-center mr-1 sm:mr-2 -ml-2"
          />
          <div class="grid grid-cols-[auto_auto] items-stretch">
            <div
              v-if="routeGame?.gameKey"
              class="py-2 px-3 flex justify-center items-center text-white bg-gs-[#1e565f] bg-linear-to-b from-[#1e565f] to-[#2c7a8a] rounded-l-lg size-8 relative cursor-pointer overflow-hidden"
              @click="isExpand = !isExpand"
            >
              <div class="absolute inset-0">
                <img
                  :src="UserIcon"
                  :alt="t('goatBtn.benefitIconAlt')"
                >
              </div>
            </div>
            <div
              class="p-2 flex justify-center items-center size-8 bg-gs-loyalty bg-linear-to-b from-gs-loyalty to-[#195059]  text-white"
              :class="{
                'rounded-l-lg pl-5 pr-5': !routeGame?.gameKey
              }"
            >
              <button-hamburger
                v-model="isExpand"
                :class="{ 'translate-y-px': !isExpand, 'translate-x-[1.5px]': isExpand }"
              />
            </div>
          </div>
        </div>
      </div>
      <nav class="w-full big:w-auto flex flex-col items-end big:flex-row big:items-center justify-between grow gap-1">
        <template v-if="isDesktop">
          <div
            class="grid transition-all duration-300 ease-in w-full whitespace-nowrap min-w-fit desktop"
          >
            <div class="overflow-hidden">
              <div class="overflow-hidden w-auto">
                <ul
                  class="flex max-h-full! flex-row menu items-center"
                >
                  <li
                    v-for="(item, itemIndex) in menuItems"
                    :key="itemIndex"
                    class="cursor-pointer px-2 py-0 text-left"
                    @click="onClickMenuLink"
                  >
                    <router-link
                      :to="item.path"
                      :title="item.title"
                      class="block cursor-pointer py-1 w-full border-0 normal-case"
                      :class="{ 'router-link-active': item.gameId && item.gameId === route.params?.gameID }"
                    >
                      {{ item.title }}
                    </router-link>
                  </li>
                  <li
                    v-if="otherItems.length"
                    class="cursor-pointer px-2 py-0 text-left"
                  >
                    <el-popover
                      class="inherit-font-size inherit-line-height"
                      :show-arrow="false"
                      popper-class="p-0 rounded-xl text-[18px] w-auto"
                      placement="bottom-start"
                    >
                      <template #reference>
                        <a
                          :class="{ 'router-link-active': isOtherPageActive }"
                          class="block cursor-pointer py-1 w-full border-0 normal-case"
                        >
                          {{ t('header.other') }}
                          <icon-ify
                            icon="mingcute:down-fill"
                            class="inline"
                          />
                        </a>
                      </template>
                      <ul class="whitespace-nowrap">
                        <li
                          v-for="(item, itemIndex) in otherItems"
                          :key="itemIndex"
                          class="cursor-pointer px-3 py-[3px] text-left border-b font-bold"
                          :class="{ 'router-link-active': item.gameId && item.gameId === route.params?.gameID }"
                          @click="onClickMenuLink"
                        >
                          <router-link
                            :to="item.path"
                            :title="item.title"
                            class="block cursor-pointer py-1 w-full :border-0 normal-case"
                          >
                            {{ item.title }}
                          </router-link>
                        </li>
                      </ul>
                    </el-popover>
                  </li>
                </ul>
              </div>
            </div>
          </div>
          <CartBtnComponent icon-class="text-2xl ml-2 mr-4" />
          <div class="ml-auto mr-0 flex items-center gap-3">
            <log-in-btn
              v-if="['game-page', 'game-product-page'].includes(String($route.meta?.pageData?.routerGroup ?? ''))"
              class="login-btn-large"
              @click="isExpand = false"
            />
            <language-selector-component />
          </div>
        </template>
        <transition
          v-else
          name="accordion-transition"
        >
          <div
            v-if="isExpand"
            class="grid transition-all duration-300 ease-in w-full whitespace-nowrap min-w-fit bg-gs-loyalty mt-3"
          >
            <div class="overflow-hidden">
              <div class="overflow-hidden w-full">
                <ul
                  class="flex flex-col menu text-white"
                  :class="{ 'w-full': isExpand }"
                >
                  <li
                    v-if="routeGame?.gameKey"
                    class="cursor-pointer px-0 text-center"
                  >
                    <div class="min-h-20 w-full flex justify-center items-center">
                      <log-in-btn
                        class="w-full"
                        @click="isExpand = false"
                      />
                    </div>
                  </li>
                  <li
                    v-for="(item, itemIndex) in menuItems"
                    :key="itemIndex"
                    class="cursor-pointer px-0 text-center relative"
                    @click="onClickMenuLink"
                  >
                    <div
                      class="absolute top-0 left-0 right-0 h-px bg-contain bg-center bg-no-repeat"
                      :style="`background-image: url('${seperatorImg}')`"
                    />
                    <router-link
                      :to="item.path"
                      :title="item.title"
                      class="block cursor-pointer py-5 w-full uppercase font-black"
                      :class="{ 'router-link-active': item.gameId && item.gameId === route.params?.gameID }"
                    >
                      {{ item.title }}
                    </router-link>
                  </li>
                  <li
                    v-for="(item, itemIndex) in otherItems"
                    :key="itemIndex"
                    class="cursor-pointer px-0 text-center relative"
                    @click="onClickMenuLink"
                  >
                    <div
                      class="absolute top-0 left-0 right-0 h-px bg-contain bg-center bg-no-repeat"
                      :style="`background-image: url('${seperatorImg}')`"
                    />
                    <router-link
                      :to="item.path"
                      :title="item.title"
                      class="block cursor-pointer py-5 w-full  uppercase font-black"
                      :class="{ 'router-link-active': item.gameId && item.gameId === route.params?.gameID }"
                    >
                      {{ item.title }}
                    </router-link>
                  </li>
                  <li
                    v-if="!['activate-goat-offer'].includes(String($route.meta?.pageData?.routerGroup ?? ''))"
                    class="relative"
                    @click="onClickMenuLink"
                  >
                    <div
                      class="absolute top-0 left-0 right-0 h-px bg-contain bg-center bg-no-repeat"
                      :style="`background-image: url('${seperatorImg}')`"
                    />
                    <router-link
                      :to="getLocalePath('activate-goat-offer')"
                      :title="t('header.goatOfferTitle')"
                      class="flex justify-center items-center py-5 w-full"
                    />
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </transition>
      </nav>
    </div>
  </header>
</template>

<style scoped lang="postcss">
.login-btn-large {
  --el-button-size: 48px;
}

header {
  box-shadow: 0 2px 12px 0 rgb(0 0 0 / 5%);
  transition: top 0.5s ease-in-out;

  &.is-hidden {
    top: -100vh !important;
  }

  ul {
    transition: max-height 0.2s ease-out;

    li {
      letter-spacing: 0.1px;

      a {
        font-weight: 700;
      }
    }
  }
}
</style>

<style lang="postcss">
body {
  padding-top: 60px;
  margin-top: 0;

  &.expand-menu {
    overflow: hidden;
  }
}

@media (width >= 900px) {
  body {
    padding-top: 81px !important;
    margin-top: 0;
  }
}
</style>
